import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import React, { createContext, useContext, useEffect, useState } from 'react'

import {APP_NAME,CLIENT_CODE, RHYBUS_ACCESS_TOKEN } from '../Configuration'
import { ChecklistMetadataLookup } from '../assets/ts/data/checklist-metadata-lookup'
import { PascodeMetadata } from '../assets/ts/data/pascode-metadata'
import { loginRequest } from "../authConfig"
import useDueOutStatusMetadata from '../hooks/dueout/use-dueout-status-metadata'
import useInquiryStatusMetadata from '../hooks/inquiry/use-inquiry-status-metadata'
import useRNLTDMetadata from '../hooks/use-RNLTD-metadata'
import useCheckListLookupMetadata from '../hooks/use-checklist-metadata-lookup'
import useCheckListMetadata from '../hooks/use-checklists-metadata'
import useCommonMetadata from '../hooks/use-common-metadata'
import usePascodeMetadata from '../hooks/use-pascode-metadata'
import useTooltipMetadata from '../hooks/use-tooltip-metadata'
import { UserContext } from '../store/user-context-provider'
import {
  ALL_SELECT_LABEL,
  ALL_SELECT_OPTION,
  ALL_SELECT_VALUE_STRING,
  SELECT_OPTION,
} from '../utils/constants/global-constants'
import { optionsTypes } from '../utils/controls/select-options-data'

interface SelectOption {
  label: string
  value: number
}

interface MetadataContextInterface {
  categories: Category[]
  categoryOptions: SelectOption[]
  categoryValue: optionsTypes
  checklistsMetadata: CheckList[]
  checklistsMetadataLookup: ChecklistMetadataLookup[]
  clearFilterValues: () => void
  componentOptions: SelectOption[]
  componentValue: optionsTypes
  components: Component[]
  dueOutStatuses: DueOutStatus[]
  fetchData: () => void
  fetchRNLTDMetdata: () => void
  inquiryStatuses: InquiryStatus[]
  isLoading: boolean
  majcomOptions: SelectOption[]
  majcomValue: optionsTypes
  majcoms: Majcom[]
  pascodeMetadata: PascodeMetadata[]
  rankOptions: SelectOption[]
  ranks: Rank[]
  rnltdMetadata: RNLTD[]
  sectionOptions: SelectOption[]
  sectionValue: optionsTypes
  sections: Section[]
  setCategoryValue: (categoryValue: optionsTypes) => void,
  setComponentValue: (componentValue: optionsTypes) => void,
  setMajcomValue: (majcomValue: optionsTypes) => void,
  setSectionValue: (sectionValue: optionsTypes) => void,
  subcategories: Subcategory[]
  subcategoriesOptions: SelectOption[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltipObj: any
  userStatusOptions: { label: string; value: string }[]
}

interface Category {
  Category: string
  CategoryCode: number
  Label: string
  OrgComponent: string
  ParentSectionCode?: number
  ServicingBy: boolean
  Value: number
}

interface CheckList {
  Code: number
  Description: string
  Name: string
  URL: null | string
}

interface Component {
  AbbrValue: string
  Component: string
  ComponentCode: number
  Label: string
  OrgComponent: string
  Value: number
}

interface DueOutStatus {
  StatusCode: string
  StatusDescription: string
}

interface InquiryStatus {
  StatusCode: string
  StatusDescription: string
}

interface Majcom {
  Label: string
  MAJCOM: string
  MAJCOMCode: number
  OrgComponent: string
  ParentComponentCode: number
  Value: number
}

interface Rank {
  AssignedComponentCode: number
  Label: string
  OrgComponent: string
  Rank: string
  RankCode: number
  SectionCode: number
  ShortName: string
  Status: string
}

interface RNLTD {
  IntervalDuration: number
  IntervalFrequency: string
  IntervalType: string
}

interface Section {
  Label: string
  OrgComponent: string
  Section: string
  SectionCode: number
  ShortName: string
  Value: number
}

interface Subcategory {
  Label: string
  OrgComponent: string
  ParentCategoryCode: number
  SubCategory: string
  SubCategoryCode: number
  Value: number
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ProfileRank {
  OrgComponent: string
  ProfileRankCode: number
  ProfileRankDescription: string
  ProfileRankShortName: string
  ProfileStatus: string
}

export const MetadataContext = createContext<MetadataContextInterface>({
  categories: [],
  categoryOptions: [],
  categoryValue: ALL_SELECT_OPTION,
  checklistsMetadata: [],
  checklistsMetadataLookup: [],
  clearFilterValues: () => {[]},
  componentOptions: [],
  componentValue: ALL_SELECT_OPTION,
  components: [],
  dueOutStatuses: [],
  fetchData: () => {[]},
  fetchRNLTDMetdata: () => {[]},
  inquiryStatuses: [],
  isLoading: true,
  majcomOptions: [],
  majcomValue: ALL_SELECT_OPTION,
  majcoms: [],
  pascodeMetadata: [],
  rankOptions: [],
  ranks: [],
  rnltdMetadata: [],
  sectionOptions: [],
  sectionValue: ALL_SELECT_OPTION,
  sections: [],
  setCategoryValue: () => undefined,
  setComponentValue: () => undefined,
  setMajcomValue: () => undefined,
  setSectionValue: () => undefined,
  subcategories: [],
  // submittedOptions[],
  subcategoriesOptions: [],
  tooltipObj: {},
  userStatusOptions: [],

})

export const MetadataContextProvider: React.FC = ({ children }) => {
  // The userProfileIsLoading state provided by UserContext indicates
  // whether we've finished initialization of the API and fetched the
  // user ID and profile.
  //
  // Any of the meta hooks that the MetadataContext provides should hold off
  // on querying the server until this basic information is established.
  // Passing this in as the "waitOnParentDependency" attribute will make sure
  // the hooks delay their initial query.
  const { userProfileIsLoading } = useContext(UserContext)

  const [categories, setCategories] = useState<Category[]>([])
  const [components, setComponents] = useState<Component[]>([])
  const [majcoms, setMajcoms] = useState<Majcom[]>([])

  const [ranks, setRanks] = useState<Rank[]>([])
  const [sections, setSections] = useState<Section[]>([])
  const [subcategories, setSubcategories] = useState<Subcategory[]>([])

  // Values for Admin View Filter Pertinence
  const [componentValue, setComponentValue] = useState<null|optionsTypes>(null)
  const [majcomValue, setMajcomValue] =  useState<null|optionsTypes>(null)
  const [sectionValue, setSectionValue] =  useState<null|optionsTypes>(null)
  const [categoryValue, setCategoryValue] =  useState<null|optionsTypes>(null)

  const clearFilterValues = () => {
    setComponentValue(null)
    setMajcomValue(null)
    setSectionValue(null)
    setCategoryValue(null)
  }
  const { accounts, instance } = useMsal()
  const isAuthenticated = useIsAuthenticated()

  const majcomOptions = [
    ALL_SELECT_OPTION,
    ...majcoms.map((m) => ({
      assignedComponentCode: m.ParentComponentCode,
      label: m.MAJCOM,
      value: m.MAJCOMCode,
    })),
  ]
  const componentOptions = [
    ALL_SELECT_OPTION,
    ...components.map((c) => ({
      label: c.Component,
      value: c.ComponentCode,
    })),
  ]

  const sectionOptions = [
    ALL_SELECT_OPTION,
    ...sections.map((s) => ({
      label: s.Section,
      value: s.SectionCode,
    })),
  ]
  const subcategoriesOptions = [
    ALL_SELECT_OPTION,
    ...subcategories.map((s) => ({
      label: s.SubCategory,
      value: s.SubCategoryCode,
    })),
  ]

  const rankOptions = [
    SELECT_OPTION,
    ...ranks.map((r) => ({
      label: r.Rank,
      value: r.RankCode,
    })),
  ]

  const statusOptions = ranks.map((m) => m.Status)
  const statusSet = new Set(statusOptions)
  const userStatusOptions = Array.from(statusSet).map((status) => ({
    label: status,
    value: status,
  }))

  const categoryOptions = [
    ALL_SELECT_OPTION,
    ...categories.map((c) => ({
      label: c.Category,
      value: c.CategoryCode,
    })),
  ]

  const { 
    commonMetadata, fetchCommonMetdata } 
    = useCommonMetadata(
    {
      isArchivedFilter: false,
    },
    userProfileIsLoading
  )

  const { pascodeMetadata } = usePascodeMetadata(
    {
      IsArchived: false,
    },
    userProfileIsLoading
  )

  const { inquiryStatusMetadata } =
    useInquiryStatusMetadata(
      {
        isArchivedFilter: null,
      },
      userProfileIsLoading
    )

  const { dueoutStatusMetadata } =
    useDueOutStatusMetadata(
      {
        isArchivedFilter: null,
      },
      userProfileIsLoading
    )

  const { tooltipMetadata } = useTooltipMetadata(
    {
      isArchived: false,
    },
    userProfileIsLoading
  )

  const { RNLTDMetadata, fetchRNLTDMetdata } = useRNLTDMetadata(
    {
      isArchived: false,
    },
    userProfileIsLoading
  )

  const { checklistsMetadataLookup } = useCheckListLookupMetadata({
    isArchivedFilter: false
  })

  const { checklistsMetadata } = useCheckListMetadata({
    isArchivedFilter: false
  })
  useEffect(() => {
    const rhybusAccessToken = async () => {
      if (isAuthenticated && accounts.length > 0) {
        await instance
          .acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          })
          .then((response) => {
            localStorage.setItem(RHYBUS_ACCESS_TOKEN, '')
            localStorage.setItem(RHYBUS_ACCESS_TOKEN, response.accessToken)
            localStorage.setItem(CLIENT_CODE,APP_NAME)
          })
      }
    }

    rhybusAccessToken()
  }, [isAuthenticated, accounts])
  useEffect(() => {
    const categoriesMetadata = commonMetadata
      .filter((m) => m.MetadataType === 'Category')
      .map((m) => ({
        Category: m.MetadataValue,
        CategoryCode: m.Code,
        Label: m.MetadataValue,
        OrgComponent: m.OrgComponent,
        ParentSectionCode: m.ParentCode,
        ServicingBy: m.AdditionalValue1,
        Value: m.Code,
      }))
      .sort((a, b) => a.Category.localeCompare(b.Category))
    setCategories(categoriesMetadata)

    const componentsMetadata = commonMetadata
      .filter((m) => m.MetadataType === 'AssignedComponent')
      .map((m) => ({
        AbbrValue: m.ShortName,
        Component: m.MetadataValue,
        ComponentCode: m.Code,
        Label: m.MetadataValue,
        OrgComponent: m.OrgComponent,
        Value: m.Code,
      }))
      .sort((a, b) => a.Component.localeCompare(b.Component))
    setComponents(componentsMetadata)

    const majcomsMetadata = commonMetadata
      .filter((m) => m.MetadataType === 'Servicing MAJCOM')
      .map((m) => ({
        Label: m.MetadataValue,
        MAJCOM: m.MetadataValue,
        MAJCOMCode: m.Code,
        OrgComponent: m.OrgComponent,
        ParentComponentCode: m.ParentCode,
        Value: m.Code,
      }))
      .sort((a, b) => a.MAJCOM.localeCompare(b.MAJCOM))
    setMajcoms(majcomsMetadata)

    const rankMetadata = commonMetadata
      .filter((m) => m.MetadataType === 'Rank')
      .map((m) => ({
        AssignedComponentCode: m.ParentCode,
        Label: m.MetadataValue,
        OrgComponent: m.OrgComponent,
        Rank: m.MetadataValue,
        RankCode: m.Code,
        SectionCode: m.Code,
        ShortName: m.ShortName,
        Status: m.AdditionalValue3,
      }))
      .sort((a, b) => a.Rank.localeCompare(b.Rank))      
    setRanks(rankMetadata)

    const sectionsMetadata = commonMetadata
      .filter((m) => m.MetadataType === 'Section')
      .map((m) => ({
        Label: m.MetadataValue,
        OrgComponent: m.OrgComponent,
        Section: m.MetadataValue,
        SectionCode: m.Code,
        ShortName : m.ShortName,
        Value: m.Code
      }))
      .sort((a, b) => a.Section.localeCompare(b.Section))
    setSections(sectionsMetadata)

    const subcategoriesMetadata = commonMetadata
      .filter((m) => m.MetadataType === 'SubCategory')
      .map((m) => ({
        Label: m.MetadataValue,
        OrgComponent: m.OrgComponent,
        ParentCategoryCode: m.ParentCode,
        SubCategory: m.MetadataValue,
        SubCategoryCode: m.Code,
        Value: m.Code,
      }))
      .sort((a, b) => a.SubCategory.localeCompare(b.SubCategory))
    setSubcategories(subcategoriesMetadata)
  }, [commonMetadata])

  const tooltipObj = tooltipMetadata.reduce((acc, item) => {
    acc[item.TooltipID] = {
      TooltipDescription: item.TooltipDescription,
      TooltipName: item.TooltipName,
    }
    return acc
  }, {})

  const rnltdMetadata = RNLTDMetadata
  .map((m) => ({
    IntervalDuration: m.IntervalDuration,
    IntervalFrequency: m.IntervalFrequency,
    IntervalType: m.IntervalType,
  }))

  const isLoading =
    !(categories.length > 0 &&
    categoryOptions.length > 0 &&
    checklistsMetadata.length > 0 &&
    checklistsMetadataLookup.length > 0 &&
    componentOptions.length > 0 &&
    components.length > 0 &&
    dueoutStatusMetadata.length > 0 &&
    inquiryStatusMetadata.length > 0 &&
    majcomOptions.length > 0 &&
    majcoms.length > 0 &&
    pascodeMetadata.length > 0 &&
    rankOptions.length > 0 &&
    ranks.length > 0 &&
    rnltdMetadata.length > 0 &&
    sectionOptions.length > 0 &&
    sections.length > 0 &&
    subcategories.length > 0 &&
    subcategoriesOptions.length > 0 &&
    Object.keys(tooltipObj).length > 0 &&
    userStatusOptions.length > 0)
  
  const fetchData = fetchCommonMetdata
  return (
    <MetadataContext.Provider
      value={{
        categories,
        categoryOptions,
        categoryValue,
        checklistsMetadata,
        checklistsMetadataLookup,
        clearFilterValues,
        componentOptions,
        componentValue,
        components,
        dueOutStatuses: dueoutStatusMetadata,
        fetchData,
        fetchRNLTDMetdata,
        inquiryStatuses: inquiryStatusMetadata,
        isLoading,
        majcomOptions,
        majcomValue,
        majcoms,
        pascodeMetadata,
        rankOptions,
        ranks,
        rnltdMetadata,
        sectionOptions,
        sectionValue,
        sections,
        setCategoryValue,
        setComponentValue,
        setMajcomValue,
        setSectionValue,
        subcategories,
        subcategoriesOptions,
        tooltipObj,
        userStatusOptions
      }}
    >
      {' '}
      {children}
    </MetadataContext.Provider>
  )
}

export { type MetadataContextInterface }
